import type {
  FlowKind,
  SimulationMetrics,
  SimulationSnapshot,
  SnapshotListener,
} from "../core/contracts";
import { FLOWS } from "./flows";

export const TOUR_PACING_FACTOR = 1.75;

const initialMetrics = (): SimulationMetrics => ({
  elapsedSeconds: 0,
  activeSessions: 1,
  logicalReadsPerSecond: 0,
  physicalReadsPerSecond: 0,
  redoBytesPerSecond: 0,
  commitsPerSecond: 0,
  completedFlows: 0,
});

export class SimulationEngine {
  private flow: FlowKind = "select";
  private activeStepIndex = -1;
  private stepElapsedMs = 0;
  private running = false;
  private paused = false;
  private speed = 0.5;
  private autoAdvance = true;
  private metrics = initialMetrics();
  private listeners = new Set<SnapshotListener>();

  subscribe(listener: SnapshotListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  run(flow: FlowKind): void {
    this.flow = flow;
    this.activeStepIndex = 0;
    this.stepElapsedMs = 0;
    this.running = true;
    this.paused = false;
    this.metrics = initialMetrics();
    this.emit();
  }

  tick(deltaMs: number): void {
    if (!this.running || this.paused || deltaMs <= 0) {
      return;
    }

    const scaledDelta = deltaMs * this.speed;
    this.stepElapsedMs += scaledDelta;
    this.metrics.elapsedSeconds += scaledDelta / 1000;

    const steps = FLOWS[this.flow];
    let active = steps[this.activeStepIndex];

    if (
      !this.autoAdvance &&
      active &&
      this.stepElapsedMs >= active.durationMs * TOUR_PACING_FACTOR
    ) {
      this.stepElapsedMs = active.durationMs * TOUR_PACING_FACTOR;
      this.paused = true;
      this.updateMetrics();
      if (this.activeStepIndex === steps.length - 1) {
        this.running = false;
        this.metrics.completedFlows += 1;
        this.metrics.activeSessions = 0;
        this.metrics.logicalReadsPerSecond = 0;
        this.metrics.physicalReadsPerSecond = 0;
      }
      this.emit();
      return;
    }

    while (
      active &&
      this.stepElapsedMs >= active.durationMs * TOUR_PACING_FACTOR
    ) {
      this.stepElapsedMs -= active.durationMs * TOUR_PACING_FACTOR;
      this.activeStepIndex += 1;
      active = steps[this.activeStepIndex];
    }

    if (!active) {
      this.running = false;
      this.activeStepIndex = steps.length - 1;
      this.stepElapsedMs = (steps.at(-1)?.durationMs ?? 0) * TOUR_PACING_FACTOR;
      this.metrics.completedFlows += 1;
      this.metrics.activeSessions = 0;
      this.metrics.logicalReadsPerSecond = 0;
      this.metrics.physicalReadsPerSecond = 0;
      this.metrics.redoBytesPerSecond = 0;
      this.metrics.commitsPerSecond = this.flow === "update" ? 1 : 0;
    } else {
      this.updateMetrics();
    }

    this.emit();
  }

  togglePause(): void {
    if (!this.running) {
      return;
    }
    this.paused = !this.paused;
    this.emit();
  }

  setSpeed(speed: number): void {
    this.speed = Math.min(2, Math.max(0.25, speed));
    this.emit();
  }

  setAutoAdvance(enabled: boolean): void {
    this.autoAdvance = enabled;
    this.emit();
  }

  seek(index: number): void {
    this.activeStepIndex = Math.max(
      0,
      Math.min(FLOWS[this.flow].length - 1, index),
    );
    this.stepElapsedMs = 0;
    this.running = true;
    this.paused = false;
    this.metrics.activeSessions = 1;
    this.updateMetrics();
    this.emit();
  }

  stepBy(offset: -1 | 1): void {
    const steps = FLOWS[this.flow];
    const requestedIndex =
      this.activeStepIndex < 0 ? 0 : this.activeStepIndex + offset;
    this.activeStepIndex = Math.min(
      steps.length - 1,
      Math.max(0, requestedIndex),
    );
    this.stepElapsedMs = 0;
    this.running = true;
    this.paused = true;
    this.metrics.activeSessions = 1;
    this.updateMetrics();
    this.emit();
  }

  reset(): void {
    this.activeStepIndex = -1;
    this.stepElapsedMs = 0;
    this.running = false;
    this.paused = false;
    this.speed = 0.5;
    this.metrics = initialMetrics();
    this.emit();
  }

  snapshot(): SimulationSnapshot {
    const steps = FLOWS[this.flow];
    const active = steps[this.activeStepIndex];
    return {
      flow: this.flow,
      steps,
      activeStepIndex: this.activeStepIndex,
      stepProgress: active
        ? Math.min(
            1,
            this.stepElapsedMs / (active.durationMs * TOUR_PACING_FACTOR),
          )
        : 0,
      running: this.running,
      paused: this.paused,
      speed: this.speed,
      autoAdvance: this.autoAdvance,
      metrics: { ...this.metrics },
    };
  }

  private updateMetrics(): void {
    const step = FLOWS[this.flow][this.activeStepIndex];
    const event = step?.event ?? "";
    const bufferActive =
      step?.phase === "buffer-cache" || step?.action === "row-source";
    this.metrics.logicalReadsPerSecond = bufferActive
      ? 680 + Math.round(this.stepElapsedMs * 0.32)
      : 34;
    this.metrics.physicalReadsPerSecond =
      step?.phase === "io" || step?.action === "block-flight"
        ? event.includes("scattered")
          ? 128
          : 32
        : 0;
    this.metrics.redoBytesPerSecond =
      this.flow === "update" &&
      [
        "transaction.undo",
        "buffer.changed",
        "redo.generated",
        "transaction.commit",
        "redo.flush",
      ].includes(event)
        ? 8192 + Math.round(this.stepElapsedMs * 2.4)
        : 0;
    this.metrics.commitsPerSecond =
      event === "transaction.commit" || event === "redo.flush" ? 1 : 0;
  }

  private emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}
